import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useApiClient } from "../../api/ApiContext";
import { locationQueryKeys, myLocationCaptureSchema } from "../../api/locationSchemas";
import type { MyLocationCapture } from "../../api/locationSchemas";
import type { GeoPoint } from "../../domain/location";
import { useOffline } from "../../offline/OfflineContext";
import { useOnlineStatus } from "../../offline/useOnlineStatus";
import { usePlatform } from "../../platform/PlatformContext";
import { positionOverallLimitMs } from "../../platform/PlatformServices";

/** Time limit of one position request (spec 12.4). */
export const positionTimeoutMs = 5_000;

/**
 * How long a statement kept on the device is used offline. Afterwards, and whenever the server
 * answers, only the answer of the server counts (a switched-off capture or a server without the
 * function stops the capture at once).
 */
export const storedCaptureValidityMs = 7 * 24 * 60 * 60 * 1000;

/** The location capture while the employer captures it: its statement is shown before the first time. */
export type ActiveLocationCapture = MyLocationCapture & {
  readonly enabled: true;
  readonly changedAt: string;
};

/** Result of {@link useLocationCapture}. */
export interface LocationCaptureControl {
  /**
   * The statement while the app captures the location when the person clocks; `null` while the
   * employer does not capture it, while it is unknown or older than a week offline, after an error
   * of the server, or without the capability, so the app then never asks (privacy by default).
   */
  readonly policy: ActiveLocationCapture | null;
  /** Whether the person saw the information (Art. 13 GDPR) of the current statement on this device. */
  readonly acknowledged: boolean;
  /** Remembers that the information of the current statement was shown. */
  readonly acknowledge: () => Promise<void>;
  /** Whether a position is being asked for right now (for the status line). */
  readonly locating: boolean;
  /**
   * Asks once for the coarse position and adds it to an own event that is already stored in the
   * outbox, as long as the event was not sent yet; clocking never waits for the position. An event
   * stored with `awaitLocation` is held back from sending until the position is added or the
   * request ends, at most for the overall limit (F-102).
   *
   * @param eventId - The stored own event.
   * @returns `true` if the location was added.
   */
  readonly attachTo: (eventId: string) => Promise<boolean>;
}

/** A statement kept on the device with the time it was fetched. */
interface StoredCapture {
  readonly capture: MyLocationCapture;
  readonly fetchedAtMs: number;
}

/**
 * Key of the last known statement of a person on this device.
 *
 * @param owner - Owner key.
 * @returns The setting key.
 */
export function locationCaptureSettingKey(owner: string): string {
  const key = `locationCapture:${owner}`;
  return key;
}

/**
 * Key of the statement whose information a person saw on this device.
 *
 * @param owner - Owner key.
 * @returns The setting key.
 */
export function locationNoticeSettingKey(owner: string): string {
  const key = `locationNotice:${owner}`;
  return key;
}

/**
 * Reads a stored statement; unreadable values count as unknown.
 *
 * @param text - The stored JSON.
 * @returns The statement with its fetch time, or `null`.
 */
function parseStored(text: string | null): StoredCapture | null {
  if (text === null) {
    return null;
  }

  try {
    const value: unknown = JSON.parse(text);
    const record =
      typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
    const parsed = myLocationCaptureSchema.safeParse(record["capture"]);
    const fetchedAtMs = record["fetchedAtMs"];
    const stored =
      parsed.success && typeof fetchedAtMs === "number"
        ? { capture: parsed.data, fetchedAtMs }
        : null;

    return stored;
  } catch {
    return null;
  }
}

/**
 * Returns the statement if the app captures the location under it.
 *
 * @param capture - The statement of the server or the device.
 * @returns The active statement, or `null`.
 */
export function activeCapture(capture: MyLocationCapture | null): ActiveLocationCapture | null {
  const active =
    capture !== null && capture.enabled && capture.changedAt !== null
      ? { ...capture, enabled: true as const, changedAt: capture.changedAt }
      : null;

  return active;
}

/**
 * Chooses the statement the app follows: the answer of the server whenever there is one; online
 * without an answer (loading, or an error such as 404 of a server without the function) none; the
 * stored one only while the device is offline and only for {@link storedCaptureValidityMs}.
 *
 * @param input - Answer, connectivity, stored statement and now.
 * @returns The statement, or `null`.
 */
export function effectiveCapture(input: {
  readonly answer: MyLocationCapture | undefined;
  readonly online: boolean;
  readonly stored: StoredCapture | null;
  readonly nowMs: number;
}): MyLocationCapture | null {
  if (input.answer !== undefined) {
    return input.answer;
  }

  // Online, a missing answer (still loading, or an error such as 404 after a rollback) means: no capture.
  const stored = input.online ? null : input.stored;
  const fresh = stored !== null && input.nowMs - stored.fetchedAtMs <= storedCaptureValidityMs;
  const capture = fresh ? stored.capture : null;

  return capture;
}

/**
 * Asks for a position and ends after the overall limit at the latest.
 *
 * @param ask - The request of the platform.
 * @returns The position, or `null`.
 */
async function askWithinLimit(ask: () => Promise<GeoPoint | null>): Promise<GeoPoint | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const limit = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      resolve(null);
    }, positionOverallLimitMs);
  });

  try {
    const position = await Promise.race([ask(), limit]);
    return position;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Whether and under which statement the clock screen captures the location when the person clocks
 * (T-175, ADR-0144). The statement comes from `GET /me/location-capture`; a copy is kept on the
 * device for a week of offline use. The information is shown again after the employer changed it.
 * The own event is stored first; the position is added to it afterwards, so it is never lost.
 *
 * @param owner - Owner key of the signed-in person, or `null`.
 * @returns The control.
 */
export function useLocationCapture(owner: string | null): LocationCaptureControl {
  const client = useApiClient();
  const offline = useOffline();
  const platform = usePlatform();
  const online = useOnlineStatus();
  const capable = platform.currentPosition !== undefined;
  const [locating, setLocating] = useState(false);
  const [stored, setStored] = useState<{
    readonly owner: string | null;
    readonly capture: StoredCapture | null;
    readonly seen: string | null;
  }>({ owner: null, capture: null, seen: null });
  const query = useQuery({
    queryKey: locationQueryKeys.mine,
    queryFn: ({ signal }) =>
      client.get("/api/v1/me/location-capture", myLocationCaptureSchema, { signal }),
    enabled: owner !== null && capable,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (owner === null) {
      return;
    }

    let cancelled = false;
    void Promise.all([
      offline.store.getSetting(locationCaptureSettingKey(owner)),
      offline.store.getSetting(locationNoticeSettingKey(owner)),
    ]).then(([capture, seen]) => {
      if (!cancelled) {
        setStored({ owner, capture: parseStored(capture), seen });
      }
    });

    return (): void => {
      cancelled = true;
    };
  }, [offline.store, owner]);

  useEffect(() => {
    if (owner !== null && query.data !== undefined) {
      const kept: StoredCapture = { capture: query.data, fetchedAtMs: offline.nowMs() };
      void offline.store.putSetting(locationCaptureSettingKey(owner), JSON.stringify(kept));
    }
  }, [offline, owner, query.data]);

  const own = stored.owner === owner ? stored : { owner, capture: null, seen: null };
  const capture = effectiveCapture({
    answer: query.isError ? undefined : query.data,
    online,
    stored: own.capture,
    nowMs: offline.nowMs(),
  });
  const policy = capable ? activeCapture(capture) : null;
  const acknowledged = policy !== null && own.seen === policy.changedAt;

  const acknowledge = useCallback(async (): Promise<void> => {
    if (owner === null || policy === null) {
      return;
    }

    await offline.store.putSetting(locationNoticeSettingKey(owner), policy.changedAt);
    setStored({ owner, capture: own.capture, seen: policy.changedAt });
  }, [offline.store, owner, own.capture, policy]);

  const attachTo = useCallback(
    async (eventId: string): Promise<boolean> => {
      // Never asked without a statement; the caller only asks while there is one.
      const ask = policy === null ? undefined : platform.currentPosition?.bind(platform);
      setLocating(true);
      let attached = false;

      try {
        const position =
          ask === undefined ? null : await askWithinLimit(() => ask(positionTimeoutMs));
        attached =
          position === null ? false : await offline.store.attachEventLocation(eventId, position);

        return attached;
      } finally {
        // The stored step waits for its location (F-102); every way out without one ends the wait,
        // so it is sent at once.
        if (!attached) {
          await offline.store.releaseEventLocation(eventId);
        }

        setLocating(false);
      }
    },
    [offline.store, platform, policy],
  );

  const control: LocationCaptureControl = { policy, acknowledged, acknowledge, locating, attachTo };

  return control;
}
